import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CurrentScopeFacade } from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
  formControlChanges$,
} from '@principle-theorem/ng-shared';
import {
  Invoice,
  invoiceBalance,
  ManualTransactionType,
  RefundHelpers,
  toINamedDocuments,
  Transaction,
  TransactionAllocation,
  withTransactions$,
} from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  InvoiceStatus,
  InvoiceType,
  IStaffer,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IInvoice,
  type IManualExtendedData,
  type IManualTransactionType,
  type IPractice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  mergeDayAndTime,
  snapshot,
  toTimestamp,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { omit } from 'lodash';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { combineLatest, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AmendTransactionDateStore } from '../transaction-components/transaction-edit-dialog/amend-transaction-date.store';
import {
  TransactionPracticeOptionMethods,
  TransactionPracticeOptions,
} from '../transaction-components/transaction-practice-options';
import { bindLimitToAmountControl } from '../transaction-amount-limit';
import { determineNewCreatedAtDate } from '../transaction-helpers';

interface ITransactionFormData
  extends Pick<
    ITransaction,
    'type' | 'status' | 'amount' | 'to' | 'from' | 'practiceRef'
  > {
  description: string;
  transactionType: INamedDocument<IManualTransactionType>;
  dateReceived?: Moment;
  attributedTo?: DocumentReference<IStaffer>;
}

class ManualTransactionForm extends TypedFormGroup<ITransactionFormData> {
  constructor() {
    super({
      type: new TypedFormControl<TransactionType>(TransactionType.Incoming),
      status: new TypedFormControl<TransactionStatus>(
        TransactionStatus.Complete
      ),
      from: new TypedFormControl<string>(),
      to: new TypedFormControl<string>(),
      amount: new TypedFormControl<number>(),
      description: new TypedFormControl<string>(),
      transactionType: new TypedFormControl<
        INamedDocument<IManualTransactionType>
      >(),
      dateReceived: new TypedFormControl<Moment>(moment()),
      practiceRef: new TypedFormControl<DocumentReference<IPractice>>(),
      attributedTo: new TypedFormControl<
        DocumentReference<IStaffer> | undefined
      >(undefined),
    });
  }
}

export interface IManualTransactionData {
  invoice: WithRef<IInvoice>;
  transaction: Partial<ITransactionFormData>;
  isRefund?: boolean;
  disableAmount?: boolean;
}

@Component({
  selector: 'pr-manual-transaction',
  templateUrl: './manual-transaction.component.html',
  styleUrls: ['./manual-transaction.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS, AmendTransactionDateStore],
  standalone: false,
})
export class ManualTransactionComponent {
  trackByStatus = TrackByFunctions.variable<TransactionStatus>();
  trackByType = TrackByFunctions.variable<TransactionType>();
  trackByTransactionType = TrackByFunctions.ref<IManualTransactionType>();
  form = new ManualTransactionForm();
  types: TransactionType[] = [
    TransactionType.Incoming,
    TransactionType.Outgoing,
  ];
  statuses: TransactionStatus[] = [
    TransactionStatus.Pending,
    TransactionStatus.Complete,
  ];
  limit$: Observable<number>;
  transactionTypes$: Observable<INamedDocument<IManualTransactionType>[]>;
  accountAdminPermission = PatientPermissions.AccountInvoiceAdmin;
  practiceOptions: TransactionPracticeOptions;
  isOutgoing: boolean = false;
  staff: INamedDocument<IStaffer>[];
  showAttributedTo: boolean;

  constructor(
    private _dialogRef: MatDialogRef<
      ManualTransactionComponent,
      ITransaction<IManualExtendedData>
    >,
    private _currentScope: CurrentScopeFacade,
    @Inject(MAT_DIALOG_DATA) public data: IManualTransactionData,
    public amendDateStore: AmendTransactionDateStore,
    private _destroyRef: DestroyRef
  ) {
    this.transactionTypes$ = this._currentScope.currentBrand$.pipe(
      filterUndefined(),
      switchMap((brand) => ManualTransactionType.all$(brand)),
      toINamedDocuments()
    );

    this.form.patchValue(this.data.transaction);

    if (this.data.disableAmount) {
      this.form.controls.amount.disable();
    }

    this.isOutgoing = this._isOutgoing();
    this.form.controls.type.setValue(
      this.isOutgoing ? TransactionType.Outgoing : TransactionType.Incoming
    );

    const { staff, showAttributedTo } =
      TransactionAllocation.getAttributedToConfig(this.data.invoice);
    this.staff = staff;
    this.showAttributedTo = showAttributedTo;

    this.limit$ = this._getLimit$(this.data);
    bindLimitToAmountControl(
      this.limit$,
      this.form.controls.amount,
      this._destroyRef
    );

    this.practiceOptions = new TransactionPracticeOptions(
      this._currentScope.currentPractice$,
      TransactionPracticeOptionMethods.toInvoicePracticeOption(
        this.data.invoice
      ),
      true
    );
    this.practiceOptions.initialValue$
      .pipe(takeUntilDestroyed())
      .subscribe((practice) =>
        this.form.controls.practiceRef.setValue(practice.ref)
      );
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    const value = this.form.getRawValue();
    if (value.amount <= 0) {
      return;
    }

    const dateReceived = value.dateReceived
      ? await determineNewCreatedAtDate(
          {
            createdAt: toTimestamp(),
            practiceRef: this.data.invoice.practice.ref,
          },
          mergeDayAndTime(value.dateReceived, moment()),
          await snapshot(this.amendDateStore.minDate$),
          await snapshot(this.amendDateStore.maxDate$)
        )
      : moment();

    const transaction = Transaction.init({
      ...omit(value, 'description', 'transactionType', 'dateReceived'),
      ...Transaction.internalReference(TransactionProvider.Manual),
      description: value.description ?? undefined,
      extendedData: {
        transactionType: value.transactionType,
      },
      practiceRef: value.practiceRef,
      createdAt: toTimestamp(dateReceived),
    });
    this._dialogRef.close(transaction);
  }

  private _getLimit$(data: IManualTransactionData): Observable<number> {
    return combineLatest([
      formControlChanges$(this.form.controls.type),
      formControlChanges$(this.form.controls.attributedTo),
    ]).pipe(
      switchMap(async ([type, attributedTo]) => {
        if (type === TransactionType.Incoming) {
          return TransactionAllocation.getMaxAllocatableForPractitioner(
            data.invoice,
            attributedTo ?? undefined
          );
        }

        if (type === TransactionType.Outgoing) {
          if (data.invoice.type === InvoiceType.CreditNote) {
            return snapshot(invoiceBalance(withTransactions$(data.invoice)));
          }
          const transactions = await snapshot(
            Invoice.transactions$(data.invoice)
          );
          return RefundHelpers.getTotalRefundRemaining(transactions);
        }

        return 0;
      })
    );
  }

  private _isOutgoing(): boolean {
    return (
      this.data.isRefund || this.data.invoice.status === InvoiceStatus.Paid
    );
  }
}
